import log from "@/helpers/log";

/**
 * Marketing attribution capture: UTM parameters plus ad click ids (gclid and
 * the other `REPORTED_CLICK_KEYS`), sourced from the URL and from Google's
 * own first-party Ads cookie (`_gcl_aw`, written by Google's tag on
 * `.metamask.io`) — not a MetaMask-written cookie.
 *
 * UTM tags and click ids only exist in the URL for the moment the user lands
 * (or briefly in Google's cookie before it expires), so they are captured
 * immediately and parked in localStorage — which must survive weeks and tab
 * closure, unlike the sessionStorage login-query stash that carries
 * `invite_code` through Auth0 and is cleared straight after login.
 *
 * The parked copy is only a holding pen. Once the user signs up the data is
 * persisted on the user row (see attributionPersistence.ts) and read back from
 * the API from then on, which is what makes a conversion in a different browser
 * still attributable.
 */

const STORAGE_KEY = "web3auth_attribution";
const SYNCED_KEY = "web3auth_attribution_synced_at";
const SCHEMA_VERSION = 2;
/** v1 (UTM-only) is a compatible subset of v2 (optional click). Still readable. */
const MIN_READABLE_SCHEMA_VERSION = 1;

/** Google's Ads tag writes this on `.metamask.io` as `GCL.<click_unix_seconds>.<gclid>`. */
const GOOGLE_CLICK_COOKIE_NAME = "_gcl_aw";
const REPORTED_CLICK_KEYS = ["gclid", "twclid", "fbclid", "msclkid", "ttclid", "li_fat_id"];
const CLICK_KEY_PATTERN = /^[a-z0-9_]{1,40}$/;
const MAX_CLICK_PARAMS = 10;

// Bounds mirror the server-side limits in backend/pkg/auth/attribution.go.
const MAX_UTM_PARAMS = 10;
/** Longest key UTM_KEY_PATTERN accepts: "utm_" plus 30 characters. */
const MAX_KEY_LENGTH = 34;
const MAX_VALUE_LENGTH = 255;
const ATTRIBUTION_WINDOW_MS = 90 * 24 * 60 * 60 * 1000;
/**
 * `_gcl_aw`'s timestamp comes from Google's clock, not the browser's, so a
 * user whose local clock runs a few minutes (or up to an hour) behind must
 * not make an otherwise-valid click look like it happened in the future.
 * Mirrors the +1h future allowance in `sanitizeTouchTime`
 * (backend/pkg/auth/attribution.go) so both sides agree on what a valid `at`
 * is. URL-sourced touches set `at` to `new Date()` and can never be more than
 * an instant ahead, so this tolerance is inert for them.
 */
const FUTURE_TOLERANCE_MS = 60 * 60 * 1000;

/**
 * `at` is the one field a lenient Date.parse would not bound: V8's legacy date
 * parser accepts an arbitrarily long trailing parenthesised comment, so a
 * crafted value would pass the window check and inflate the signup request body
 * past the server's 1MB limit, failing the signup. Requiring ISO-8601 also
 * matches the backend's strict RFC3339 parse, so a value we accept is a value
 * the server will keep.
 */
const AT_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;
const MAX_AT_LENGTH = 40;

function isValidTouchTime(value: unknown): value is string {
  return typeof value === "string" && value.length <= MAX_AT_LENGTH && AT_PATTERN.test(value);
}

/**
 * Any utm_* parameter is captured, so a campaign launched with a brand new tag
 * is collected before anyone asks for it. Keys are kept under their incoming
 * name with no renaming: utm_id and utm_campaignid are distinct
 * campaign-identifier parameters, not aliases of each other.
 */
const UTM_KEY_PATTERN = /^utm_[a-z0-9_]{1,30}$/;

/**
 * Parameters sent to Segment/Mixpanel as named properties. Anything captured
 * but not listed here is collapsed into a single `utm_extra` property, so a
 * typo (`utm_souce`) or a crafted URL cannot permanently pollute the Mixpanel
 * lexicon. The raw values stay in the DB, so promoting a key here later can be
 * backfilled.
 */
const REPORTED_UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_id", "utm_campaignid", "utm_ad_group", "utm_term", "utm_content"];

export type AttributionTouch = {
  utm?: Record<string, string>;
  click?: Record<string, string>;
  at: string;
};

export type CampaignForCli = { utm?: Record<string, string>; click?: Record<string, string>; at?: string };

export type UserAttribution = {
  initial?: AttributionTouch;
  latest?: AttributionTouch;
  v?: number;
};

/**
 * Truncates and strips characters that have no business in a campaign tag.
 * These values are attacker-controlled and end up in the GTM dataLayer, where a
 * Custom HTML tag writing a dataLayer variable into the DOM would turn
 * `utm_source=<img onerror=...>` into stored XSS. Cheap to defuse here.
 */
function clean(value: string, max = MAX_VALUE_LENGTH): string {
  // Markup-significant and control characters only. Spaces, hyphens and
  // underscores are legitimate in campaign names and must survive.
  /* eslint-disable-next-line no-control-regex */
  const stripped = value.replace(/[<>"'`\\]|[\u0000-\u001f\u007f]/g, "").trim();
  return stripped.length > max ? stripped.slice(0, max) : stripped;
}

function normalizeUtmKey(key: string): string | undefined {
  const lowered = key.trim().toLowerCase();
  if (!lowered || lowered.length > MAX_KEY_LENGTH) return undefined;
  return UTM_KEY_PATTERN.test(lowered) ? lowered : undefined;
}

function normalizeClickKey(key: string): string | undefined {
  const lowered = key.trim().toLowerCase();
  if (!lowered || lowered.length > MAX_KEY_LENGTH) return undefined;
  return CLICK_KEY_PATTERN.test(lowered) && REPORTED_CLICK_KEYS.includes(lowered) ? lowered : undefined;
}

/** The click key `_gcl_aw` maps to, routed through the same whitelist as the URL capture path so both agree if it ever changes. */
const GOOGLE_CLICK_KEY = normalizeClickKey("gclid");

/** Exact-name cookie lookup on `document.cookie`. Never throws. */
function readCookie(name: string): string | undefined {
  try {
    const entry = document.cookie.split("; ").find((c) => c.startsWith(`${name}=`));
    if (!entry) return undefined;
    return decodeURIComponent(entry.slice(name.length + 1));
  } catch (error) {
    log.debug("unable to read cookie", error);
    return undefined;
  }
}

/**
 * Parses Google's `_gcl_aw` value: `GCL.<click_unix_seconds>.<gclid>`. Only
 * this exact 3-part shape is accepted — Google's format for repeated clicks
 * (multiple ids in one cookie) is not stable across gtag versions, so it is
 * deliberately not handled here; anything that isn't the 3-part shape is
 * treated as absent rather than guessed at.
 */
function parseGclCookie(value: string): { id: string; at: string } | undefined {
  const parts = value.split(".");
  if (parts.length !== 3 || parts[0] !== "GCL" || !/^\d+$/.test(parts[1])) return undefined;

  const id = clean(parts[2]);
  if (!id) return undefined;

  const seconds = Number(parts[1]);
  if (!Number.isFinite(seconds)) return undefined;

  const clickDate = new Date(seconds * 1000);
  if (Number.isNaN(clickDate.getTime())) return undefined;
  const at = clickDate.toISOString();

  // isWithinWindow enforces both bounds here: too old (beyond
  // ATTRIBUTION_WINDOW_MS) or too far in the future (beyond Google's
  // clock-skew tolerance, FUTURE_TOLERANCE_MS).
  if (!isWithinWindow({ at })) return undefined;

  return { id, at };
}

/** Reads the ad-click touch from Google's own cookies. The `at` is the real click time, not read time. */
function readGoogleClickTouch(): AttributionTouch | undefined {
  if (!GOOGLE_CLICK_KEY) return undefined;

  const raw = readCookie(GOOGLE_CLICK_COOKIE_NAME);
  if (!raw) return undefined;

  const gcl = parseGclCookie(raw);
  if (!gcl) return undefined;

  return { click: { [GOOGLE_CLICK_KEY]: gcl.id }, at: gcl.at };
}

/** Builds a touch from the current URL, or undefined when there is no campaign signal to record. */
function readTouchFromUrl(): AttributionTouch | undefined {
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};
  const click: Record<string, string> = {};
  params.forEach((rawValue, rawKey) => {
    const value = clean(rawValue);
    if (!value) return;
    const utmKey = normalizeUtmKey(rawKey);
    if (utmKey && Object.keys(utm).length < MAX_UTM_PARAMS) utm[utmKey] = value;
    const clickKey = normalizeClickKey(rawKey);
    if (clickKey && Object.keys(click).length < MAX_CLICK_PARAMS) click[clickKey] = value;
  });

  const cookie = readGoogleClickTouch();
  const hasUrlClick = Object.keys(click).length > 0;
  const hasUrlSignal = Object.keys(utm).length > 0 || hasUrlClick;
  // Cookie-only landings are a real capture path: Google's cookie carries a
  // gclid that never appears on this URL. An untagged reload must not replay
  // that cookie when the parked latest is already newer.
  if (!hasUrlSignal && isAtLeastAsRecent(readStorage()?.latest, cookie)) return undefined;

  // `_gcl_aw` lasts up to 90 days. A later Twitter/Meta/email landing with
  // UTMs (and no Google click id) must not inherit that stale gclid, or
  // last-touch and Ads enrichment credit the wrong campaign. Merge the cookie
  // only on a cookie-only landing or when the URL already carries `gclid`.
  const urlHasGoogleClick = Boolean(GOOGLE_CLICK_KEY && click[GOOGLE_CLICK_KEY]);
  const cookieForMerge = !hasUrlSignal || urlHasGoogleClick ? cookie : undefined;

  // URL keys take the 10-key budget first. Spreading the cookie first would
  // let a full cookie occupy every slot so last-touch URL UTMs/click ids are
  // dropped when getStoredAttribution rebuilds the map.
  const mergedUtm = mergePreferIncoming(utm, cookieForMerge?.utm, MAX_UTM_PARAMS);
  const mergedClick = mergePreferIncoming(click, cookieForMerge?.click, MAX_CLICK_PARAMS);
  if (!mergedUtm && !mergedClick) return undefined;

  return {
    ...(mergedUtm ? { utm: mergedUtm } : {}),
    ...(mergedClick ? { click: mergedClick } : {}),
    // !hasUrlSignal means the URL contributed neither utm nor click, so
    // mergedUtm/mergedClick above can only be non-empty via the cookie (the
    // only other source); the `!mergedUtm && !mergedClick` return above
    // already ruled out "cookie is also empty", so cookie is defined here.
    at: hasUrlSignal ? new Date().toISOString() : cookie!.at,
  };
}

/** Last-touch keys first, then fill remaining slots from `existing`, capped at `max`. */
function mergePreferIncoming(
  incoming: Record<string, string> | undefined,
  existing: Record<string, string> | undefined,
  max: number
): Record<string, string> | undefined {
  const out: Record<string, string> = {};
  for (const source of [incoming, existing]) {
    if (!source) continue;
    for (const [key, value] of Object.entries(source)) {
      if (key in out || Object.keys(out).length >= max) continue;
      out[key] = value;
    }
  }
  return Object.keys(out).length ? out : undefined;
}

/** True when `existing` is in-window and not older than `incoming`. */
function isAtLeastAsRecent(existing: AttributionTouch | undefined, incoming: AttributionTouch | undefined): boolean {
  return Boolean(incoming && existing && isWithinWindow(existing) && Date.parse(incoming.at) <= Date.parse(existing.at));
}

function isWithinWindow(touch: AttributionTouch | undefined): boolean {
  if (!isValidTouchTime(touch?.at)) return false;
  const touchedAt = Date.parse(touch.at);
  const age = Date.now() - touchedAt;
  return Number.isFinite(touchedAt) && age >= -FUTURE_TOLERANCE_MS && age <= ATTRIBUTION_WINDOW_MS;
}

function readStorage(): UserAttribution | undefined {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as UserAttribution) : undefined;
  } catch (error) {
    // Private mode, blocked storage, or a corrupt blob: treat as absent.
    log.debug("unable to read stored attribution", error);
    return undefined;
  }
}

/**
 * Captures the current URL's campaign parameters. The initial touch is only set
 * once; the latest touch is overwritten on every tagged visit.
 */
export function captureAttribution(): void {
  const touch = readTouchFromUrl();
  if (!touch) return;

  const stored = readStorage();
  // Last-touch is recency-ordered. A stale cookie replay must not replace a
  // newer parked latest (and must not re-trigger sync with the older payload).
  if (isAtLeastAsRecent(stored?.latest, touch)) return;

  const next: UserAttribution = {
    initial: isWithinWindow(stored?.initial) ? stored?.initial : touch,
    latest: touch,
    v: SCHEMA_VERSION,
  };

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (error) {
    log.debug("unable to store attribution", error);
  }
}

/**
 * Rebuilds a touch from a whitelist of fields with their types checked, rather
 * than trusting the parsed blob. The blob is attacker-writable (XSS, an
 * extension, a stale schema from a previous release) and is sent verbatim in
 * the signup request body, where a wrong field type would fail the whole
 * request decode and block account creation.
 */
function rebuildTouch(value: unknown): AttributionTouch | undefined {
  if (!value || typeof value !== "object") return undefined;
  const raw = value as Record<string, unknown>;
  if (!isValidTouchTime(raw.at)) return undefined;

  const rebuildMap = (input: unknown, allowKey: (key: string) => string | undefined) => {
    if (!input || typeof input !== "object") return undefined;
    const result: Record<string, string> = {};
    Object.entries(input as Record<string, unknown>).forEach(([key, entry]) => {
      if (typeof entry !== "string" || Object.keys(result).length >= MAX_UTM_PARAMS) return;
      const allowed = allowKey(key);
      const cleaned = clean(entry);
      if (allowed && cleaned) result[allowed] = cleaned;
    });
    return Object.keys(result).length ? result : undefined;
  };

  const touch: AttributionTouch = { at: raw.at };
  const utm = rebuildMap(raw.utm, normalizeUtmKey);
  const click = rebuildMap(raw.click, normalizeClickKey);
  if (utm) touch.utm = utm;
  if (click) touch.click = click;

  return touch.utm || touch.click ? touch : undefined;
}

/** Returns the parked attribution, dropping touches outside the attribution window. */
export function getStoredAttribution(): UserAttribution | undefined {
  const stored = readStorage();
  // Unknown future versions are discarded. v1 is a compatible subset of v2
  // (click is optional), so parked UTMs from before this deploy stay readable.
  if (!stored || stored.v == null || stored.v < MIN_READABLE_SCHEMA_VERSION || stored.v > SCHEMA_VERSION) {
    return undefined;
  }

  const attribution: UserAttribution = { v: SCHEMA_VERSION };
  const initial = rebuildTouch(stored.initial);
  const latest = rebuildTouch(stored.latest);
  if (isWithinWindow(initial)) attribution.initial = initial;
  if (isWithinWindow(latest)) attribution.latest = latest;

  return attribution.initial || attribution.latest ? attribution : undefined;
}

function syncMarker(attribution: UserAttribution): string {
  return attribution.latest?.at ?? attribution.initial?.at ?? "";
}

/** True when the parked latest touch has already been sent to the backend, so logins don't re-PATCH on every page load. */
export function isSynced(attribution: UserAttribution): boolean {
  const marker = syncMarker(attribution);
  if (!marker) return false;
  try {
    return window.localStorage.getItem(SYNCED_KEY) === marker;
  } catch {
    return false;
  }
}

export function markSynced(attribution: UserAttribution): void {
  const marker = syncMarker(attribution);
  if (!marker) return;
  try {
    window.localStorage.setItem(SYNCED_KEY, marker);
  } catch (error) {
    log.debug("unable to mark attribution as synced", error);
  }
}

/**
 * Clears the synced marker. Called on logout so the next user in this browser
 * is not treated as already synced, which would silently drop their
 * attribution.
 */
export function clearAttributionSyncMarker(): void {
  try {
    window.localStorage.removeItem(SYNCED_KEY);
  } catch (error) {
    log.debug("unable to clear attribution sync marker", error);
  }
}

/**
 * First-touch properties, sourced from the user's row via the API rather than
 * localStorage so they resolve on a device that never saw the ad click. This is
 * what makes a cross-browser conversion attributable.
 *
 * These are attached to every *event* rather than set as a Mixpanel profile
 * property, for two reasons. First, both Segment sources in this app are
 * entity-keyed — `orgAnalytics` by team id, `projectAnalytics` by project id —
 * so there is no user-keyed source whose profile could hold them. Second, event
 * properties are frozen at fire time, while a breakdown by a profile property
 * uses its *current* value with no history and would silently re-attribute past
 * events.
 */
export function toInitialTouchProperties(attribution: UserAttribution | undefined): Record<string, string> {
  const initial = attribution?.initial;
  if (!initial) return {};

  const properties: Record<string, string> = {};
  REPORTED_UTM_KEYS.forEach((key) => {
    const value = initial.utm?.[key];
    if (value) properties[`initial_${key}`] = value;
  });
  if (initial.at) properties.initial_touch_at = initial.at;

  return properties;
}

/**
 * Latest-touch properties, read from this browser's parked copy since they
 * describe the current session.
 */
export function toLatestTouchProperties(): Record<string, string> {
  const latest = getStoredAttribution()?.latest;
  if (!latest) return {};

  const properties: Record<string, string> = {};
  const extra: string[] = [];

  Object.entries(latest.utm ?? {}).forEach(([key, value]) => {
    if (REPORTED_UTM_KEYS.includes(key)) properties[key] = value;
    else extra.push(`${key}=${value}`);
  });
  if (extra.length) properties.utm_extra = clean(extra.join("&"));

  return properties;
}

/**
 * Bare utm_* key/value pairs for the GTM virtual-page URL query — no
 * `initial_`/`latest_` prefix, since these become literal query parameters
 * GTM's History Change trigger reads off the URL.
 *
 * Best-effort per key: prefers this browser's latest (session) touch, then
 * the server-synced latest touch (covers a device with no local storage but
 * a prior synced session elsewhere), then falls back to the server-sourced
 * initial touch, so a conversion happening in a browser that never saw the
 * ad click still carries the acquiring campaign in the URL.
 */
export function toUtmQueryParams(profileAttribution: UserAttribution | undefined): Record<string, string> {
  const initialUtm = profileAttribution?.initial?.utm ?? {};
  const profileLatestUtm = profileAttribution?.latest?.utm ?? {};
  const localLatestUtm = getStoredAttribution()?.latest?.utm ?? {};

  const params: Record<string, string> = {};
  REPORTED_UTM_KEYS.forEach((key) => {
    const value = localLatestUtm[key] ?? profileLatestUtm[key] ?? initialUtm[key];
    if (value) params[key] = value;
  });

  return params;
}

/** Flat, whitelisted props for the `Agentic Login Completed` event. */
export function toCampaignEventProps(): Record<string, string> {
  const touch = getStoredAttribution()?.latest;
  if (!touch) return {};
  const props: Record<string, string> = {};
  REPORTED_CLICK_KEYS.forEach((k) => {
    const v = touch.click?.[k];
    if (v) props[k] = v;
  });
  REPORTED_UTM_KEYS.forEach((k) => {
    const v = touch.utm?.[k];
    if (v) props[k] = v;
  });
  if (touch.at) props.campaign_touch_at = touch.at;
  return props;
}

/** Compact object encoded into the CLI token 4th segment. */
export function getCampaignForCli(): CampaignForCli | undefined {
  const touch = getStoredAttribution()?.latest;
  if (!touch || (!touch.utm && !touch.click)) return undefined;
  return {
    ...(touch.utm ? { utm: touch.utm } : {}),
    ...(touch.click ? { click: touch.click } : {}),
    at: touch.at,
  };
}

/** Runs a server-sourced map's keys and values through the same whitelist as the localStorage reader before it is allowed anywhere near `mergePreferIncoming`. */
function sanitizeServerMap(
  input: Record<string, string> | undefined,
  allowKey: (key: string) => string | undefined
): Record<string, string> | undefined {
  if (!input) return undefined;
  const result: Record<string, string> = {};
  Object.entries(input).forEach(([key, value]) => {
    const allowed = allowKey(key);
    const cleaned = typeof value === "string" ? clean(value) : "";
    if (allowed && cleaned) result[allowed] = cleaned;
  });
  return Object.keys(result).length ? result : undefined;
}

/**
 * True when both click maps describe the same paid click: they share at least
 * one click id and no shared key disagrees. A newer local `gclid` (or a
 * UTM-only local touch) must not inherit Ads-resolved names from another
 * touch stored on the user row.
 */
function sameClickIds(local: Record<string, string> | undefined, server: Record<string, string> | undefined): boolean {
  if (!local || !server) return false;
  let shared = 0;
  for (const [key, value] of Object.entries(local)) {
    const other = server[key];
    if (other === undefined) continue;
    if (other !== value) return false;
    shared += 1;
  }
  return shared > 0;
}

/**
 * Merges this browser's parked campaign with the server-resolved one returned
 * alongside the CLI token (see `CLITokenResult.campaign`). Local wins per key
 * — this browser's session is the freshest last-touch — the server only fills
 * gaps in `utm` and `click`, and only when both sides describe the same click.
 * `at` prefers local, falling back to the server's (validated; dropped rather
 * than carried through if it fails validation).
 *
 * The server is a trusted caller, but this path shares the same whitelist and
 * `clean()` as the localStorage reader so a typo'd or unexpected key can't
 * reach the token from either source.
 */
export function mergeCampaignForCli(local: CampaignForCli | undefined, server: CampaignForCli | undefined): CampaignForCli | undefined {
  const serverUtm = sanitizeServerMap(server?.utm, normalizeUtmKey);
  const serverClick = sanitizeServerMap(server?.click, normalizeClickKey);
  const hasLocal = Boolean(local?.utm || local?.click);
  const hasServer = Boolean(serverUtm || serverClick);

  if (!hasLocal) {
    if (!hasServer) return undefined;
    const at = isValidTouchTime(server?.at) ? server?.at : undefined;
    return {
      ...(serverUtm ? { utm: serverUtm } : {}),
      ...(serverClick ? { click: serverClick } : {}),
      ...(at ? { at } : {}),
    };
  }

  const fillFromServer = sameClickIds(local?.click, serverClick);
  const utm = fillFromServer ? mergePreferIncoming(local?.utm, serverUtm, MAX_UTM_PARAMS) : local?.utm;
  const click = fillFromServer ? mergePreferIncoming(local?.click, serverClick, MAX_CLICK_PARAMS) : local?.click;
  if (!utm && !click) return undefined;

  const at = local?.at || (fillFromServer && isValidTouchTime(server?.at) ? server?.at : undefined);

  return {
    ...(utm ? { utm } : {}),
    ...(click ? { click } : {}),
    ...(at ? { at } : {}),
  };
}
